import moment from 'moment';
import { useState } from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import FormControlLabel from '@mui/material/FormControlLabel';

import { RouterLink } from 'src/routes/components';

import {
  AssistantAction,
  AssistantActionEdits,
  useCancelAssistantActionMutation,
  useConfirmAssistantActionMutation,
} from 'src/services/ai/ai-assistant';

import Iconify from 'src/components/iconify';

const TASK_TYPES = [
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'to_do', label: 'To do' },
  { value: 'general', label: 'General' },
];

const STATUS_TEXT: Record<string, { severity: 'success' | 'warning' | 'error' | 'info'; text: string }> = {
  done: { severity: 'success', text: 'Done' },
  partly_done: { severity: 'warning', text: 'Partly done' },
  failed: { severity: 'error', text: 'Not done' },
  cancelled: { severity: 'info', text: 'Cancelled. Nothing was changed.' },
  expired: { severity: 'info', text: 'This waited too long and lapsed. Nothing was changed. Ask again if you still want it.' },
};

/** The edit form's starting values, from what the assistant proposed. */
function initialEdits(action: AssistantAction): AssistantActionEdits {
  const values = action.summary.values ?? {};
  const due = values.due_at ? moment(values.due_at) : null;
  return {
    title: values.title,
    type: values.type,
    due_date: due ? due.format('YYYY-MM-DD') : '',
    due_time: due ? due.format('HH:mm') : '',
    text: values.text,
    notify_candidate: values.notify_candidate,
  };
}

/** Only the fields the card lets the recruiter change, so an untouched field is never re-sent. */
function editsToSend(action: AssistantAction, edits: AssistantActionEdits): AssistantActionEdits {
  const out: AssistantActionEdits = {};
  action.summary.editable.forEach((field) => {
    const value = edits[field];
    if (value !== undefined) (out as any)[field] = value;
  });
  return out;
}

/**
 * A change the assistant proposed, waiting for the recruiter (JT1-1780, decision D1). Shows exactly
 * what will change and on which record. Nothing happens until Confirm; Edit adjusts the details
 * first; Cancel discards it. Afterwards it shows what was done, with a link to the record.
 */
export default function AssistantActionCard({
  action,
  onChange,
}: {
  action: AssistantAction;
  onChange: (action: AssistantAction) => void;
}) {
  const [confirmAction, { isLoading: confirming }] = useConfirmAssistantActionMutation();
  const [cancelAction, { isLoading: cancelling }] = useCancelAssistantActionMutation();
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState<AssistantActionEdits>(() => initialEdits(action));
  const [problem, setProblem] = useState<string | null>(null);

  const { summary } = action;
  const pending = action.status === 'pending';
  const editable = new Set(summary.editable ?? []);

  const confirm = async () => {
    setProblem(null);
    try {
      onChange(await confirmAction({ id: action.id, edits: editing ? editsToSend(action, edits) : {} }).unwrap());
      setEditing(false);
    } catch (error: any) {
      setProblem(error?.data?.message ?? 'That did not work. Please try again.');
    }
  };

  const cancel = async () => {
    setProblem(null);
    try {
      onChange(await cancelAction(action.id).unwrap());
    } catch (error: any) {
      setProblem(error?.data?.message ?? 'That did not work. Please try again.');
    }
  };

  const status = STATUS_TEXT[action.status];
  const resultUrl = action.result?.url ?? summary.record?.url ?? null;

  return (
    <Box
      data-testid="assistant-action-card"
      sx={{ border: '1px solid', borderColor: pending ? 'primary.main' : 'divider', borderRadius: 1.5, p: 1.5, my: 1, bgcolor: 'background.paper' }}
    >
      <Stack direction="row" alignItems="center" gap={1} mb={1}>
        <Iconify icon={pending ? 'solar:shield-check-bold' : 'solar:check-circle-bold'} width={18} sx={{ color: pending ? 'primary.main' : 'text.disabled' }} />
        <Typography variant="subtitle2">{summary.title}</Typography>
      </Stack>

      {summary.record && (
        <Typography variant="caption" color="text.secondary" component="div" mb={0.75}>
          On{' '}
          {summary.record.url ? (
            <Link component={RouterLink} href={summary.record.url}>{summary.record.name}</Link>
          ) : (
            summary.record.name
          )}
        </Typography>
      )}

      {!editing && (
        <Stack spacing={0.5}>
          {summary.fields.map((field) => (
            <Stack key={field.label} direction="row" gap={1}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 84, flexShrink: 0 }}>{field.label}</Typography>
              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{field.value}</Typography>
            </Stack>
          ))}
          {summary.applications && summary.applications.length > 1 && (
            <Stack spacing={0.25} mt={0.5}>
              {summary.applications.map((application, i) => (
                <Typography key={i} variant="caption">
                  {application.url ? <Link component={RouterLink} href={application.url}>{application.candidate}</Link> : application.candidate}
                  {application.job ? `, ${application.job}` : ''} ({application.from})
                </Typography>
              ))}
            </Stack>
          )}
        </Stack>
      )}

      {editing && (
        <Stack spacing={1.25} mt={0.5}>
          {editable.has('title') && (
            <TextField size="small" label="Task" value={edits.title ?? ''} onChange={(e) => setEdits({ ...edits, title: e.target.value })} />
          )}
          {editable.has('type') && (
            <TextField select size="small" label="Type" value={edits.type ?? 'to_do'} onChange={(e) => setEdits({ ...edits, type: e.target.value })}>
              {TASK_TYPES.map((type) => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
            </TextField>
          )}
          {(editable.has('due_date') || editable.has('due_time')) && (
            <Stack direction="row" gap={1}>
              <TextField size="small" type="date" label="Due date" InputLabelProps={{ shrink: true }} value={edits.due_date ?? ''} onChange={(e) => setEdits({ ...edits, due_date: e.target.value })} sx={{ flex: 1 }} />
              <TextField size="small" type="time" label="Time" InputLabelProps={{ shrink: true }} value={edits.due_time ?? ''} onChange={(e) => setEdits({ ...edits, due_time: e.target.value })} sx={{ width: 120 }} />
            </Stack>
          )}
          {editable.has('text') && (
            <TextField size="small" multiline minRows={3} maxRows={10} label="Note" value={edits.text ?? ''} onChange={(e) => setEdits({ ...edits, text: e.target.value })} />
          )}
          {editable.has('notify_candidate') && (
            <FormControlLabel
              control={<Switch size="small" checked={edits.notify_candidate !== false} onChange={(e) => setEdits({ ...edits, notify_candidate: e.target.checked })} />}
              label={<Typography variant="caption">Send the rejection email</Typography>}
            />
          )}
        </Stack>
      )}

      {summary.warning && pending && !editing && <Alert severity="warning" sx={{ mt: 1, py: 0 }}>{summary.warning}</Alert>}
      {problem && <Alert severity="error" sx={{ mt: 1, py: 0 }}>{problem}</Alert>}

      {pending ? (
        <Stack direction="row" gap={1} mt={1.25} justifyContent="flex-end">
          <Button size="small" color="inherit" disabled={confirming || cancelling} onClick={editing ? () => setEditing(false) : cancel}>
            {editing ? 'Back' : 'Cancel'}
          </Button>
          {!editing && editable.size > 0 && (
            <Button size="small" variant="outlined" disabled={confirming || cancelling} onClick={() => setEditing(true)}>Edit</Button>
          )}
          <LoadingButton size="small" variant="contained" loading={confirming} disabled={cancelling} onClick={confirm}>
            Confirm
          </LoadingButton>
        </Stack>
      ) : (
        status && (
          <Alert severity={status.severity} sx={{ mt: 1, py: 0 }}>
            {action.status === 'done' || action.status === 'partly_done' ? (action.result?.message ?? status.text) : status.text}
            {action.error && action.status !== 'cancelled' ? ` ${action.error}` : ''}
            {resultUrl && (action.status === 'done' || action.status === 'partly_done') && (
              <>
                {' '}
                <Link component={RouterLink} href={resultUrl}>Open</Link>
              </>
            )}
          </Alert>
        )
      )}
    </Box>
  );
}
